import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Check, ChevronDown } from 'lucide-react';

export const LanguageSwitcher: React.FC = () => {
  const { language, setLanguage } = useLanguage();

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const buttonId = useId();
  const menuId = useId();

  const options = useMemo(
    () => [
      { value: 'sl' as const, label: 'Slovenščina', flag: '🇸🇮' },
      { value: 'en' as const, label: 'English', flag: '🇬🇧' },
    ],
    [],
  );

  const active = options.find((o) => o.value === language) ?? options[0];

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      const el = menuRef.current;
      if (!el) return;
      if (e.target instanceof Node && !el.contains(e.target)) {
        setOpen(false);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        id={buttonId}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white/80 px-3 py-2 text-sm font-medium text-slate-700 shadow-sm backdrop-blur transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
      >
        <span className="text-base leading-none" aria-hidden="true">{active.flag}</span>
        <ChevronDown size={16} className={`text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-labelledby={buttonId}
          className="absolute left-0 mt-2 w-44 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
        >
          {options.map((o) => {
            const selected = o.value === language;
            return (
              <button
                key={o.value}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => {
                  setLanguage(o.value);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <span className="text-base leading-none" aria-hidden="true">{o.flag}</span>
                <span className="flex-1 truncate">{o.label}</span>
                {selected && <Check size={16} className="text-primary-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
